import React, { useCallback, useEffect, useState } from "react";
import api, { describeError } from "@/lib/api";
import { Kpi, Panel, Skeleton, ErrorState, inr, num, bytes, selectClass } from "@/components/platform/ui";

const PRESETS = [
  { id: "all", label: "All time", days: null },
  { id: "7", label: "7 days", days: 7 },
  { id: "30", label: "30 days", days: 30 },
  { id: "90", label: "90 days", days: 90 },
  { id: "custom", label: "Custom", days: null },
];

const isoDay = (d) => d.toISOString().slice(0, 10);

export default function PlatformDashboard() {
  const [preset, setPreset] = useState("all");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    const params = {};
    const p = PRESETS.find((x) => x.id === preset);
    if (p?.days) {
      params.start = isoDay(new Date(Date.now() - p.days * 86400000));
      params.end = isoDay(new Date());
    } else if (preset === "custom") {
      if (custom.start) params.start = custom.start;
      if (custom.end) params.end = custom.end;
    }
    try {
      const { data } = await api.get("/platform/stats", { params });
      setData(data);
    } catch (e) {
      setErr(describeError(e, "Could not load platform statistics."));
    } finally {
      setLoading(false);
    }
  }, [preset, custom]);

  useEffect(() => { load(); }, [load]);

  const noRange = preset === "all" || (preset === "custom" && !custom.start && !custom.end);
  const inRange = (v) => (v === null || v === undefined
    ? (noRange ? "Select a date range" : "Not available")
    : num(v));

  if (loading && !data) return <Skeleton rows={6} testid="owner-dashboard-loading" />;
  if (err && !data) return <ErrorState message={err} onRetry={load} testid="owner-dashboard-error" />;

  const o = data.organizations, u = data.users, s = data.subscriptions,
        r = data.revenue, g = data.usage;

  return (
    <div data-testid="owner-dashboard" className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-tight text-navy sm:text-4xl">Platform overview</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Advocate Diary across every organisation.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="owner-date-range">
          {PRESETS.map((p) => (
            <button key={p.id} type="button" onClick={() => setPreset(p.id)}
                    data-testid={`owner-range-${p.id}`}
                    className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      preset === p.id ? "bg-navy text-white" : "border border-border bg-white text-navy/70 hover:text-navy"
                    }`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {preset === "custom" && (
        <div className="flex flex-wrap items-center gap-3" data-testid="owner-custom-range">
          <input type="date" value={custom.start} data-testid="owner-range-start" className={selectClass}
                 onChange={(e) => setCustom((c) => ({ ...c, start: e.target.value }))} />
          <span className="text-sm text-muted-foreground">to</span>
          <input type="date" value={custom.end} data-testid="owner-range-end" className={selectClass}
                 onChange={(e) => setCustom((c) => ({ ...c, end: e.target.value }))} />
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi testid="kpi-total-organizations" label="Total organizations" value={num(o.total)}
             sub={`New in range: ${inRange(o.new_in_range)}`} />
        <Kpi testid="kpi-individual-advocates" label="Individual advocates" value={num(o.individual_advocates)} />
        <Kpi testid="kpi-law-firms" label="Law firms" value={num(o.law_firms)} />
        <Kpi testid="kpi-total-users" label="Total users" value={num(u.total)}
             sub={`New in range: ${inRange(u.new_in_range)}`} />
        <Kpi testid="kpi-active-7" label="Active users · 7 days" value={num(u.active_last_7_days)}
             sub="Based on last sign-in" />
        <Kpi testid="kpi-active-30" label="Active users · 30 days" value={num(u.active_last_30_days)}
             sub="Based on last sign-in" />
        <Kpi testid="kpi-mrr" label="MRR" value={inr(r.mrr)} sub="Active paid plans only" />
        <Kpi testid="kpi-collected" label="Collected revenue" value={inr(r.collected_total)}
             sub={`${num(r.failed_count)} failed · ${inr(r.failed_total)}`} />
        <Kpi testid="kpi-cases" label="Cases" value={num(g.cases)} />
        <Kpi testid="kpi-clients" label="Clients" value={num(g.clients)} />
        <Kpi testid="kpi-hearings" label="Hearings" value={num(g.hearings)} />
        <Kpi testid="kpi-documents" label="Documents" value={num(g.documents)}
             sub={`${bytes(g.storage_bytes)} stored`} />
      </div>

      <Panel title="Subscriptions" testid="owner-subscriptions-panel">
        <div className="grid grid-cols-2 gap-x-6 gap-y-4 p-5 sm:grid-cols-4">
          {[
            ["Free", s.free, "free"], ["Trialing", s.trialing, "trialing"],
            ["Active Pro", s.active_pro, "active-pro"], ["Active Law Firm", s.active_law_firm, "active-law-firm"],
            ["Cancelled", s.cancelled, "cancelled"], ["Expired", s.expired, "expired"],
            ["Past due", s.past_due, "past-due"], ["Suspended", s.suspended, "suspended"],
          ].map(([label, value, id]) => (
            <div key={id} data-testid={`sub-${id}`}>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
              <p className="mt-1 font-display text-2xl text-navy">{num(value)}</p>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="Activity" testid="owner-activity-panel">
        <div className="p-5">
          <p className="font-display text-3xl text-navy" data-testid="owner-active-orgs">
            {num(g.organizations_active_last_30_days)}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">organisations active in the last 30 days</p>
          <p className="mt-3 rounded-md bg-secondary px-3 py-2 text-xs text-muted-foreground"
             data-testid="owner-activity-basis">
            {g.activity_basis}
          </p>
        </div>
      </Panel>

      <p className="text-xs text-muted-foreground" data-testid="owner-revenue-basis">{r.basis}</p>
    </div>
  );
}
